import { splitEvery, values } from 'ramda';
import { DATA_GRID_SEARCH_PARAMS_ID, DEFAULT_ROW_INDEX, DataGridSearchParams, GRID_ENTRY_SEPARATOR, PAGE_ENTRY_SEPARATOR, PAGE_GRID_SEPARATOR, PAGE_SEARCH_PARAMS_ID } from '../../constants/urlSync';

/**
 * @about Utility function to get dataGrid info from URL
 * @returns DataGridSearchParams
 * @example
 *  URL = http://metada.localhost:4200/vehicles?dataGrids=vehicle,6f5fc539-...,1|
 *
 *  const returnValue = getSearchParamsByGridCode('vehicle');
 *  //=> {gridCode: 'vehicle', queryId: '6f5fc539-...', rowIndex: '1'}
 */
export const getSearchParamsByGridCode = (gridCode: string): DataGridSearchParams => {
  const searchParams = getFormattedSearchParams();
  return searchParams[gridCode];
};

/**
 * @about Utility function to get dataGrid info from URL.
 *
 * Supports two URL formats:
 * - **New (pipe-separated):** `dataGrids=vehicle,abc,1|customers,def,2|`
 * - **Legacy (comma-only):** `dataGrids=vehicle,abc,1` (splitEvery(3) fallback)
 *
 * The parser auto-detects which format is in use.
 * Page is stored separately in `&page=gridCode:N,...`.
 *
 * @returns Record keyed by gridCode
 */
export const getFormattedSearchParams = (): Record<string, DataGridSearchParams> => {
  const url = new URL(window.location.href);
  const rawValue = url.searchParams.getAll(DATA_GRID_SEARCH_PARAMS_ID)[0] ?? '';
  if (!rawValue) {
    return {};
  }
  const formattedSearchParams: Record<string, DataGridSearchParams> = {};

  // New pipe-separated format: vehicle,abc,1|customers,def,2|
  if (rawValue.includes(GRID_ENTRY_SEPARATOR)) {
    const gridEntries = rawValue.split(GRID_ENTRY_SEPARATOR);
    gridEntries.forEach(entry => {
      const parts = entry.split(',');
      if (parts[0]) {
        formattedSearchParams[parts[0]] = {
          gridCode: parts[0],
          queryId: parts[1],
          rowIndex: parts[2]
        };
      }
    });
    return formattedSearchParams;
  }

  // Legacy comma-only format: vehicle,abc,1,customers,def,2
  // Uses splitEvery(3) to remain backward compatible with existing URLs
  const rawDataGridSearchParams = splitEvery(3)(rawValue.split(','));
  rawDataGridSearchParams.forEach(searchParam => {
    if (searchParam[0]) {
      formattedSearchParams[searchParam[0]] = {
        gridCode: searchParam[0],
        queryId: searchParam[1],
        rowIndex: searchParam[2]
      };
    }
  });
  return formattedSearchParams;
};

/**
 * Returns true when a grid entry has enough valid data to be serialized into the URL.
 * Entries with missing, empty, or literal `'undefined'` queryId are skipped — this
 * prevents ghost entries like `1,undefined,1` from polluting the URL.
 */
const isValidGridEntry = (entry: DataGridSearchParams): boolean => Boolean(entry.gridCode) && Boolean(entry.queryId) && entry.queryId !== 'undefined';

/**
 * Serializes all grid search params into a flat URL-safe string.
 * Uses pipe `|` as grid-entry separator and comma `,` as field separator.
 * Always appends a trailing `|` so the parser recognises new format even
 * when there is only one grid entry.
 *
 * Output format: `vehicle,abc,1|customers,def,2|`
 */
export const convertToSearchParams = (newSearchParams: Record<string, DataGridSearchParams>, omitSearchParams?: boolean): Record<string, string> => {
  const entries = values(newSearchParams).filter(searchParam => isValidGridEntry(searchParam)).map(searchParam => `${searchParam.gridCode},${searchParam.queryId},${searchParam.rowIndex ?? DEFAULT_ROW_INDEX}`);

  // Always append a trailing separator so the parser recognises new 4-field format
  // even when there is only one grid entry (no join produces a pipe otherwise).
  // The parser's `if (parts[0])` guard silently drops the empty trailing token.
  const params = entries.length > 0 ? entries.join(GRID_ENTRY_SEPARATOR) + GRID_ENTRY_SEPARATOR : '';
  if (omitSearchParams) {
    return {
      [DATA_GRID_SEARCH_PARAMS_ID]: params
    };
  }
  return {
    ...Object.fromEntries(new URLSearchParams(window.location.search)),
    [DATA_GRID_SEARCH_PARAMS_ID]: params
  };
};

/**
 * Reads the current 1-based page number for a specific grid from the `&page=` URL param.
 *
 * Format: `page=vehicle-opensearch:2,customers:1`
 *
 * @returns 1-based page number, or undefined if not present
 */
export const getPageFromUrl = (gridCode: string): number | undefined => {
  const url = new URL(window.location.href);
  const raw = url.searchParams.get(PAGE_SEARCH_PARAMS_ID) ?? '';
  if (!raw) {
    return undefined;
  }
  const entries = raw.split(PAGE_GRID_SEPARATOR);
  for (const entry of entries) {
    const separatorIndex = entry.indexOf(PAGE_ENTRY_SEPARATOR);
    if (separatorIndex === -1) {
      continue;
    }
    const code = entry.slice(0, separatorIndex);
    const pageStr = entry.slice(separatorIndex + 1);
    if (code === gridCode) {
      const parsed = parseInt(pageStr, 10);
      return isNaN(parsed) ? undefined : parsed;
    }
  }
  return undefined;
};

/**
 * Writes the 1-based page number for a specific grid into the `&page=` URL param via replaceState.
 *
 * Format: `page=vehicle-opensearch:2,customers:1`
 *
 * Other grids' page entries are preserved. The `dataGrids` param is not touched.
 */
export const setPageInUrl = (gridCode: string, page: number): void => {
  const url = new URL(window.location.href);
  const raw = url.searchParams.get(PAGE_SEARCH_PARAMS_ID) ?? '';
  const entries = raw ? raw.split(PAGE_GRID_SEPARATOR) : [];

  // Build a map of existing entries (preserves other grids)
  const pageMap = new Map<string, number>();
  entries.forEach(entry => {
    const separatorIndex = entry.indexOf(PAGE_ENTRY_SEPARATOR);
    if (separatorIndex === -1) {
      return;
    }
    const code = entry.slice(0, separatorIndex);
    const pageStr = entry.slice(separatorIndex + 1);
    const parsed = parseInt(pageStr, 10);
    if (code && !isNaN(parsed)) {
      pageMap.set(code, parsed);
    }
  });

  // Skip replaceState entirely when the URL already has the correct page value —
  // avoids redundant history mutations when the same page is written twice
  // (e.g. re-renders that don't actually change the page number).
  if (pageMap.get(gridCode) === page) {
    return;
  }
  pageMap.set(gridCode, page);
  const newPageValue = Array.from(pageMap.entries()).map(([code, p]) => `${code}${PAGE_ENTRY_SEPARATOR}${p}`).join(PAGE_GRID_SEPARATOR);

  // Preserve all existing search params, update only the page param
  const allParams = Object.fromEntries(new URLSearchParams(window.location.search));
  allParams[PAGE_SEARCH_PARAMS_ID] = newPageValue;
  const currentUrlWithoutSearchParams = url.origin + url.pathname;
  window.history.replaceState(null, '', currentUrlWithoutSearchParams + '?' + new URLSearchParams(allParams));
};